(() => {
  const COLOR_NAME = /color|colour|颜色|顏色/i;
  const RETRY_LIMIT = 100;

  const getFeaturedMediaId = (variant) => {
    const id =
      variant?.featured_media?.id ||
      variant?.featured_image?.id;

    return id ? String(id) : "";
  };

  const getOptionValue = (variant, index) => {
    return (
      variant?.options?.[index] ??
      variant?.[`option${index + 1}`] ??
      ""
    );
  };

  const getColorOptionIndex = (productInfo) => {
    const fields = Array.from(
      productInfo.querySelectorAll("[data-picker-field]")
    );

    return fields.findIndex((field) =>
      COLOR_NAME.test(field.dataset.optionName || "")
    );
  };

  /*
   * 只获取已经被 Zest 正式注册的 Flickity 实例。
   * 主题原生 updateMedia() 也是使用这个方式获取实例。
   */
  const getRegisteredFlickity = (element) => {
    if (!element || !window.FoxTheme?.Flickity?.data) {
      return null;
    }

    return window.FoxTheme.Flickity.data(element) || null;
  };

  /*
   * 更新完成后允许使用组件内部保存的实例作为备用。
   */
  const getAnyFlickity = (element) => {
    return (
      getRegisteredFlickity(element) ||
      element?.slider?.instance ||
      null
    );
  };

  /*
   * 必须等待主图和缩略图轮播都完成初始化，
   * 否则 Zest 在删除缩略图后可能无法重新插入。
   */
  const isSliderReady = (gallery) => {
    const sliderExpected =
      gallery.mediaLayout === "carousel" ||
      window.FoxThemeSettings?.isMobile;

    if (!sliderExpected) {
      return true;
    }

    const mediaList = gallery.querySelector(
      ".f-product__media-list"
    );

    const thumbnailList = gallery.querySelector(
      ".f-product__media-thumbnails"
    );

    const mainFlickity =
      getRegisteredFlickity(mediaList);

    if (
      !mainFlickity ||
      !mainFlickity.cells?.length
    ) {
      return false;
    }

    /*
     * 只要页面中存在缩略图结构，
     * 就必须等到缩略图 Flickity 正式注册完成。
     */
    if (thumbnailList) {
      const thumbnailFlickity =
        getRegisteredFlickity(thumbnailList);

      if (
        !thumbnailFlickity ||
        !thumbnailFlickity.cells?.length
      ) {
        return false;
      }
    }

    return true;
  };

  /*
   * 包装 Zest 原生 updateMedia()。
   * 筛选图片前等待轮播，筛选后重新计算主图和缩略图。
   */
  const patchMediaCounter = (productInfo) => {
    if (productInfo.__autoVariantMediaPatched) {
      return;
    }

    if (typeof productInfo.updateMedia !== "function") {
      return;
    }

    const originalUpdateMedia =
      productInfo.updateMedia;

    productInfo.updateMedia = function (variant) {
      const gallery = this.productMedia;

      if (!gallery) {
        return originalUpdateMedia.call(
          this,
          variant
        );
      }

      const mediaList = gallery.querySelector(
        ".f-product__media-list"
      );

      const thumbnailList = gallery.querySelector(
        ".f-product__media-thumbnails"
      );

      const sliderExpected =
        gallery.mediaLayout === "carousel" ||
        window.FoxThemeSettings?.isMobile;

      const mainFlickity =
        getRegisteredFlickity(mediaList);

      const thumbnailFlickity =
        getRegisteredFlickity(thumbnailList);

      const sliderNotReady =
        sliderExpected &&
        (
          !mainFlickity ||
          (
            thumbnailList &&
            !thumbnailFlickity
          )
        );

      /*
       * 轮播还没有准备好时不执行原生筛选，
       * 避免缩略图被删空。
       */
      if (sliderNotReady) {
        this.__autoVariantMediaRetries =
          (this.__autoVariantMediaRetries || 0) + 1;

        if (
          this.__autoVariantMediaRetries > 100
        ) {
          console.warn(
            "[Auto variant media] 主图或缩略图轮播初始化超时。"
          );

          gallery.removeAttribute(
            "data-media-loading"
          );

          return;
        }

        if (
          !mainFlickity &&
          typeof mediaList?.initSlider === "function"
        ) {
          mediaList.initSlider();
        }

        if (
          thumbnailList &&
          !thumbnailFlickity &&
          typeof thumbnailList.initSlider ===
            "function"
        ) {
          thumbnailList.initSlider();
        }

        clearTimeout(
          this.__autoVariantMediaRetry
        );

        this.__autoVariantMediaRetry =
          window.setTimeout(() => {
            this.updateMedia(variant);
          }, 150);

        return;
      }

      this.__autoVariantMediaRetries = 0;

      let result;

      try {
        result = originalUpdateMedia.call(
          this,
          variant
        );
      } catch (error) {
        console.error(
          "[Auto variant media] 更新产品媒体失败，正在重试：",
          error
        );

        this.__autoVariantMediaRetries =
          (this.__autoVariantMediaRetries || 0) + 1;

        if (
          this.__autoVariantMediaRetries <= 20
        ) {
          clearTimeout(
            this.__autoVariantMediaRetry
          );

          this.__autoVariantMediaRetry =
            window.setTimeout(() => {
              this.updateMedia(variant);
            }, 180);
        } else {
          gallery.removeAttribute(
            "data-media-loading"
          );
        }

        return;
      }

      /*
       * 等待 Zest 完成 DOM 插入，再重新计算轮播。
       */
      window.setTimeout(() => {
        const currentMainFlickity =
          getAnyFlickity(mediaList);

        const currentThumbnailFlickity =
          getAnyFlickity(thumbnailList);

        if (currentMainFlickity) {
          currentMainFlickity.reloadCells?.();
          currentMainFlickity.resize?.();
          currentMainFlickity.reposition?.();

          if (
            currentMainFlickity.cells?.length
          ) {
            currentMainFlickity.select?.(
              0,
              false,
              true
            );
          }
        }

        if (currentThumbnailFlickity) {
          currentThumbnailFlickity.reloadCells?.();
          currentThumbnailFlickity.resize?.();
          currentThumbnailFlickity.reposition?.();

          if (
            currentThumbnailFlickity.cells?.length
          ) {
            currentThumbnailFlickity.select?.(
              0,
              false,
              true
            );
          }
        }

        /*
         * 同时调用 Zest 自定义组件的更新方法。
         */
        mediaList?.update?.();
        thumbnailList?.update?.();

        const total =
          gallery.querySelectorAll(
            ".f-product__media"
          ).length;

        const currentCounter =
          gallery.querySelector(
            ".flickity-counter--current"
          );

        const totalCounter =
          gallery.querySelector(
            ".flickity-counter--total"
          );

        if (currentCounter) {
          currentCounter.textContent =
            total ? "1" : "0";
        }

        if (totalCounter) {
          totalCounter.textContent =
            String(total);
        }

        gallery.removeAttribute(
          "data-media-loading"
        );
      }, 120);

      return result;
    };

    productInfo.__autoVariantMediaPatched =
      true;
  };

  const setupAutoGroups = (
    gallery,
    currentVariant,
    attempt = 0
  ) => {
    if (!gallery) {
      return;
    }

    if (
      gallery.dataset.autoVariantMediaReady ===
      "true"
    ) {
      return;
    }

    const productInfo =
      gallery.closest("product-info");

    const variants =
      gallery.variantData ||
      gallery.getVariantData?.();

    const mediaNodes =
      gallery.domNodes?.medias;

    const isReady =
      productInfo &&
      Array.isArray(variants) &&
      variants.length &&
      mediaNodes?.length &&
      isSliderReady(gallery);

    if (!isReady) {
      if (attempt < RETRY_LIMIT) {
        window.setTimeout(() => {
          setupAutoGroups(
            gallery,
            currentVariant,
            attempt + 1
          );
        }, 100);
      }

      return;
    }

    /*
     * 获取所有主图媒体及其排列顺序。
     */
    const mediaRecords =
      Array.from(mediaNodes)
        .map((node, index) => ({
          id: String(
            node.dataset.mediaId || ""
          ),
          type:
            node.dataset.mediaType ||
            "image",
          index
        }))
        .filter((media) => media.id);

    const mediaIndexById = new Map(
      mediaRecords.map((media) => [
        media.id,
        media.index
      ])
    );

    const colorIndex =
      getColorOptionIndex(productInfo);

    /*
     * 保存每个颜色对应的代表图片。
     * 相同颜色的不同尺码可以共用同一代表图。
     */
    const colorAnchors = new Map();

    if (colorIndex >= 0) {
      variants.forEach((variant) => {
        const mediaId =
          getFeaturedMediaId(variant);

        if (
          mediaId &&
          mediaIndexById.has(mediaId)
        ) {
          const colorValue = String(
            getOptionValue(
              variant,
              colorIndex
            )
          )
            .trim()
            .toLowerCase();

          colorAnchors.set(
            colorValue,
            mediaId
          );
        }
      });
    }

    const resolveAnchor = (variant) => {
      const directMediaId =
        getFeaturedMediaId(variant);

      /*
       * 优先使用当前变体直接绑定的代表图。
       */
      if (
        directMediaId &&
        mediaIndexById.has(directMediaId)
      ) {
        return directMediaId;
      }

      /*
       * 当前尺码没有单独绑定图片时，
       * 继承同颜色其他尺码的代表图。
       */
      if (colorIndex >= 0) {
        const colorValue = String(
          getOptionValue(
            variant,
            colorIndex
          )
        )
          .trim()
          .toLowerCase();

        return (
          colorAnchors.get(colorValue) ||
          ""
        );
      }

      return "";
    };

    /*
     * 按代表图片在商品媒体中的位置排序。
     */
    const anchors = Array.from(
      new Set(
        variants
          .map(resolveAnchor)
          .filter(Boolean)
      )
    ).sort(
      (first, second) =>
        mediaIndexById.get(first) -
        mediaIndexById.get(second)
    );

    if (anchors.length < 2) {
      console.warn(
        "[Auto variant media] 至少需要两个颜色变体绑定代表图片。"
      );

      gallery.removeAttribute(
        "data-media-loading"
      );

      return;
    }

    /*
     * 每一个颜色分组：
     * 从当前颜色代表图开始，
     * 到下一个颜色代表图之前结束。
     */
    const groupsByAnchor = new Map();

    anchors.forEach((anchor, index) => {
      const startIndex =
        mediaIndexById.get(anchor);

      const endIndex =
        index + 1 < anchors.length
          ? mediaIndexById.get(
              anchors[index + 1]
            )
          : mediaRecords.length;

      const groupMediaIds =
        mediaRecords
          .slice(startIndex, endIndex)
          .filter(
            (media) =>
              media.type === "image"
          )
          .map((media) => media.id);

      groupsByAnchor.set(
        anchor,
        groupMediaIds
      );
    });

    const allImageIds =
      mediaRecords
        .filter(
          (media) =>
            media.type === "image"
        )
        .map((media) => media.id);

    /*
     * 为每一个具体变体生成图片分组映射。
     */
    const mapping = variants.map(
      (variant) => {
        const anchor =
          resolveAnchor(variant);

        return {
          id: Number(variant.id),
          media:
            groupsByAnchor.get(anchor) ||
            allImageIds
        };
      }
    );

    /*
     * 注入与 FoxKit 相同的数据结构，
     * 复用 Zest 原生图片筛选代码。
     */
    productInfo.variantGroupImages = {
      enable: true,
      mapping
    };

    productInfo.allMediaIds =
      allImageIds;

    productInfo.dataset.autoVariantMediaGroups =
      "true";

    gallery.dataset.autoVariantMediaReady =
      "true";

    patchMediaCounter(productInfo);

    const selectedVariantId = Number(
      productInfo.querySelector(
        'input[name="id"]'
      )?.value
    );

    const selectedVariant =
      currentVariant ||
      variants.find(
        (variant) =>
          Number(variant.id) ===
          selectedVariantId
      ) ||
      gallery.currentVariant ||
      variants[0];

    if (
      selectedVariant &&
      typeof productInfo.updateMedia ===
        "function"
    ) {
      productInfo.updateMedia(
        selectedVariant
      );
    }
  };

  /*
   * 产品媒体组件初始化完成时执行。
   */
  document.addEventListener(
    "product-media:loaded",
    (event) => {
      const gallery =
        event.target.closest?.(
          "media-gallery"
        );

      setupAutoGroups(
        gallery,
        event.detail?.currentVariant
      );
    }
  );

  /*
   * 防止当前脚本加载时间晚于产品媒体初始化。
   */
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      document
        .querySelectorAll(
          "media-gallery"
        )
        .forEach((gallery) => {
          setupAutoGroups(
            gallery,
            gallery.currentVariant
          );
        });
    }
  );

  /*
   * 支持 Shopify 主题编辑器重新加载模块。
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      event.target
        .querySelectorAll?.(
          "media-gallery"
        )
        .forEach((gallery) => {
          setupAutoGroups(
            gallery,
            gallery.currentVariant
          );
        });
    }
  );
})();